<template>
    <app-layout>
      <Head title="Contacts"/>
        <template #header>
            <div class="md:flex justify-between items-center pb-1">
                <div class="mr-4 md:mr-5 flex justify-start items-center">
                   <PageBackHeader
                        :href="'/contacts/view/' + contact.id"
                        label="Contacts"
                        :subtitle="contact.full_name + ': Edit'"
                    />
                </div>
            </div>
        </template>

        <div class="max-w-7xl mx-auto flex mb-6">
          <div class="w-full p-2 sm:px-6 lg:px-8">
            <jet-form-section @submitted="updateContactDetails">
              <template #title>
                  Contact Details
              </template>

              <template #description>
                  Update this contact's information.
              </template>

              <template #form>
                  <!-- Name -->
                  <div class="col-span-6 sm:col-span-4">
                      <jet-label for="first_name" value="First Name" />
                      <jet-input
                          id="first_name"
                          type="text"
                          class="mt-1 block w-full"
                          v-model="form.first_name"
                          autocomplete="off"
                          maxlength="150"
                      />
                    <p
                    v-if="$page.props.errors.first_name"
                      class="text-sm text-red-500"
                      >
                      {{ $page.props.errors.first_name }}
                    </p>
                      <jet-input-error v-if="form.errors.updateContactDetails" :message="form.errors.updateContactDetails.first_name" class="mt-2" />
                  </div>

                  <div class="col-span-6 sm:col-span-4">
                      <jet-label for="last_name" value="Last Name" />
                      <jet-input
                          id="last_name"
                          type="text"
                          class="mt-1 block w-full"
                          v-model="form.last_name"
                          autocomplete="off"
                      />
                    <p
                    v-if="$page.props.errors.last_name"
                      class="text-sm text-red-500"
                      >
                      {{ $page.props.errors.last_name }}
                    </p>
                      <!-- <jet-input-error v-if="form.errors.updateContactDetails" :message="form.errors.updateContactDetails.last_name" class="mt-2" /> -->
                  </div>

                  <div class="col-span-6 sm:col-span-4">
                        <maz-phone-number-input
                            id="phone-number"
                            v-model="form.phone_number"
                            :only-countries="['US']"
                            :required="true"
                        />
                    <p
                        v-if="$page.props.errors.phone_number"
                        class="text-sm text-red-500"
                    >
                        {{ $page.props.errors.phone_number }}
                    </p>
                  </div>

                  <div class="col-span-6 sm:col-span-4">
                      <jet-label for="assigned_to" value="Assigned to" />
                      <select
                          id="assigned_to"
                          v-model="form.assigned_to_tra_id"
                          class="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                          :disabled="loadingUsers"
                      >
                          <option value="">Unassigned</option>
                          <option
                              v-for="user in users"
                              :key="user.tra_id"
                              :value="user.tra_id"
                          >
                              {{ user.name }}
                          </option>
                      </select>
                      <p v-if="loadingUsers" class="text-sm text-gray-500 mt-1">Loading users...</p>
                      <p
                          v-if="$page.props.errors.assigned_to_tra_id"
                          class="text-sm text-red-500 mt-1"
                      >
                          {{ $page.props.errors.assigned_to_tra_id }}
                      </p>

                    <template v-if="customFields.length">
                        <!-- Custom Fields Separator -->
                        <div class="col-span-6 sm:col-span-4 mt-6">
                            <div class="flex items-center">
                                <hr class="flex-grow border-t border-gray-300">
                                <span class="px-3 text-gray-600 text-sm font-medium">Custom Fields</span>
                                <hr class="flex-grow border-t border-gray-300">
                            </div>
                        </div>
                        <div 
                            v-for="field in customFields" 
                            :key="field.tra_id"
                            class="col-span-6 sm:col-span-4 mt-4"
                        >
                            <jet-label 
                            :for="field.tra_id" 
                            :value="field.name" 
                            />
                            
                            <!-- Text and Number inputs -->
                            <template v-if="['Text', 'Number', 'URL'].includes(field.type)">
                            <jet-input
                                :id="field.tra_id"
                                type="text"
                                class="mt-1 block w-full"
                                v-model="form.custom_fields[field.tra_id]"
                                autocomplete="off"
                            />
                            </template>

                            <!-- Date input -->
                            <template v-else-if="field.type === 'Date'">
                                <jet-input
                                    :id="field.tra_id"
                                    type="date"
                                    class="mt-1 block w-full"
                                    v-model="form.custom_fields[field.tra_id]"
                                    autocomplete="off"
                                />
                            </template>

                            <!-- For Address type -->
                            <template v-else-if="field.type === 'Address'">
                                <AddressField
                                    v-model="form.custom_fields[field.tra_id]"
                                    :errors="$page.props.errors[`custom_fields.${field.tra_id}`]"
                                />
                            </template>

                            <!-- Error handling -->
                            <p
                            v-if="$page.props.errors[`custom_fields.${field.tra_id}`]"
                            class="text-sm text-red-500"
                            >
                            {{ $page.props.errors[`custom_fields.${field.tra_id}`] }}
                            </p>
                        </div>
                    </template>
                  </div>
              </template>

              <template #actions>
                  <jet-action-message :on="form.recentlySuccessful" class="mr-3">
                      Saved.
                  </jet-action-message>

                  <jet-button
                      :class="{ 'opacity-25': form.processing }"
                      :disabled="form.processing"
                  >
                      Save
                  </jet-button>
              </template>
            </jet-form-section>
          </div>
        </div>
    </app-layout>
</template>

<script>
import JetButton from "./../../Jetstream/Button";
import JetFormSection from "./../../Jetstream/FormSection";
import JetInput from "./../../Jetstream/Input";
import JetInputError from "./../../Jetstream/InputError";
import JetLabel from "./../../Jetstream/Label";
import JetActionMessage from "./../../Jetstream/ActionMessage";
import JetSecondaryButton from "./../../Jetstream/SecondaryButton";
import timezones from "../../helpers/timezones";
import AddressField from "@/components/CustomFields/AddressField";
import axios from "axios";

export default {
    components: {
        JetActionMessage,
        JetButton,
        JetFormSection,
        JetInput,
        JetInputError,
        JetLabel,
        JetSecondaryButton,
        //VuePhoneNumberInput,
        AddressField
    },

    props: {
        data: Array,
        links: Object,
        meta: Object,
        contact: Object
    },

    data() {
        // Pre-initialize the custom_fields structure
        const customFieldsInit = {};
        this.$page.props.custom_fields.data.forEach(field => {
            if (this.contact.custom_fields[field.tra_id]) {
                customFieldsInit[field.tra_id] = this.contact.custom_fields[field.tra_id];
            } else if (field.type === 'Address') {
                customFieldsInit[field.tra_id] = {country: 'US'};
            } else {
                customFieldsInit[field.tra_id] = '';
            }
        });

        return {
            users: [],
            loadingUsers: false,
            form: this.$inertia.form({
                first_name: this.contact.first_name,
                last_name: this.contact.last_name,
                phone_number: this.contact.phone_number,
                assigned_to_tra_id: null,
                custom_fields: customFieldsInit,
            })
        };
    },

    mounted() {
        this.loadUsers();
    },

    computed: {
        customFields() {
            return this.$page.props.custom_fields.data;
        }
    },
    methods: {
        async loadUsers() {
            this.loadingUsers = true;
            try {
                const response = await axios.get(route('organization.users'));
                this.users = response.data;
            } catch (error) {
                this.$toasted.show('Failed to load users', {
                    position: 'bottom-right',
                    type: 'error',
                    duration: 2000,
                });
            } finally {
                this.loadingUsers = false;
            }
        },


        updateContactDetails() {
          this.form.post(
            route("contacts.edit.submit", {
                id: this.contact.id,
            }),
            {
              onError: errors => {
                  this.$toasted.show(
                      `There's been an error updating your contact, please try again.`,
                      {
                          position: "bottom-right",
                          type: "error",
                          duration: 1000
                      }
                  );
              }
            },
              {
                bag: "updateProfileInformation",
                resetOnSuccess: false
              }
          );
        },
    },

    watch: {
        users(newUsers) {
            // Once users are loaded, find and set the current assigned user's tra_id
            if (newUsers.length > 0 && this.contact.assigned_to_user) {
                const assignedUser = newUsers.find(user => user.name === this.contact.assigned_to_user);
                if (assignedUser) {
                    this.form.assigned_to_tra_id = assignedUser.tra_id;
                }
            }
        }
    }
};
</script>
